import {Fragment, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {notification} from "antd";
import ActionBar from "~/layout/ActionBar";
import {Button, Loading, Modal, Icon} from "~/components";
import {
	employeeEvaluationActions,
	employeeEvaluationItemsSelector,
	employeeEvaluationLoadingSelector,
	employeeEvaluationErrorSelector,
	employeeEvaluationPaginationSelector,
	employeeEvaluationFilterSelector,
} from "../employeeEvaluationSlice";
import {employeeEvaluationApi, userApi} from "~/api";
import {apiError, handleRequest} from "~/utils";
import {useCan, useGroup} from "~/hooks";
import {EvaluationTable, EvaluationSearchBar, SetCycleModal} from "../components";

function EmployeeEvaluation() {

	const dispatch    = useDispatch();
	const items       = useSelector(employeeEvaluationItemsSelector);
	const loading     = useSelector(employeeEvaluationLoadingSelector);
	const error       = useSelector(employeeEvaluationErrorSelector);
	const pagination  = useSelector(employeeEvaluationPaginationSelector);
	const filter      = useSelector(employeeEvaluationFilterSelector);

	const canView = useCan('evaluationView');

	const listGroup = useGroup();

	const [openModal, setOpenModal] = useState({setCycle: false});
	const [cycleUser, setCycleUser] = useState(null);

	const handleModalOpen  = (modal) => setOpenModal(prev => ({...prev, [modal]: true}));
	const handleModalClose = (modal) => setOpenModal(prev => ({...prev, [modal]: false}));

	useEffect(() => {
		if (canView) {
			dispatch(employeeEvaluationActions.fetchData({...filter}));
		}
	}, [filter, canView]);

	if (error) {
		notification.error({message: 'Lỗi', description: error});
	}

	const handleReLoading = () => {
		dispatch(employeeEvaluationActions.fetchData({...filter}));
	};

	//Đánh giá lại 1 bản ghi
	const handleReEvaluate = async (item) => {
		let [err, response] = await handleRequest(employeeEvaluationApi.reEvaluate(item.id));
		let message = apiError('Đánh giá lại thất bại', err, response);
		if (!message) {
			notification.success({message: 'Thành công', description: 'Đánh giá lại thành công'});
			dispatch(employeeEvaluationActions.update(response.data));
		}
	};

	//Tạm khóa / mở khóa nhân viên (thao tác trên user)
	const handleLocked = async (item) => {
		if (!item?.userId) return;
		if (item.user?.locked === 1) {
			notification.error({message: 'Lỗi', description: 'Nhân viên này hiện đã bị tạm khóa'});
			return;
		}
		let [err, response] = await handleRequest(userApi.locked(item.userId));
		let message = apiError('Khóa nhân viên thất bại', err, response);
		if (!message) {
			notification.success({message: 'Thành công', description: 'Khóa nhân viên thành công'});
			dispatch(employeeEvaluationActions.update({id: item.id, user: {...item.user, locked: 1}}));
		}
	};

	const handleUnLocked = async (item) => {
		if (!item?.userId) return;
		if (item.user?.locked === 0) {
			notification.error({message: 'Lỗi', description: 'Nhân viên này không bị khóa'});
			return;
		}
		let [err, response] = await handleRequest(userApi.unlocked(item.userId));
		let message = apiError('Mở khóa nhân viên thất bại', err, response);
		if (!message) {
			notification.success({message: 'Thành công', description: 'Mở khóa nhân viên thành công'});
			dispatch(employeeEvaluationActions.update({id: item.id, user: {...item.user, locked: 0}}));
		}
	};

	//Thiết lập chu kỳ đánh giá cho nhân viên của dòng đang chọn
	const handleSetCycleOpen = (item) => {
		setCycleUser({
			id: item.userId,
			firstname: item.user?.firstname,
			lastname: item.user?.lastname,
		});
		handleModalOpen('setCycle');
	};

	const handleSetCycleSuccess = () => {
		handleModalClose('setCycle');
		handleReLoading();
	};

	//Search & pagination
	const handleSearchChange = (newFilter) => {
		dispatch(employeeEvaluationActions.setFilterWithDebounce(newFilter));
	};

	const handleFilterChange = (newFilter) => {
		dispatch(employeeEvaluationActions.setFilter(newFilter));
	};

	const handlePaginationChange = (page) => {
		dispatch(employeeEvaluationActions.setFilter({...filter, page}));
	};

	return (
		<Fragment>
			<ActionBar title="Đánh giá nhân viên">
				<Button outline onClick={handleReLoading}>{Icon.reload}</Button>
			</ActionBar>

			<EvaluationSearchBar
				filter={filter}
				listGroup={listGroup}
				onSearchChange={handleSearchChange}
				onChange={handleFilterChange}
			/>

			<div className="container">
				<div className="content">
					{loading && <Loading/>}
					{items && (
						<EvaluationTable
							items={items}
							pagination={pagination}
							onPaginationChange={handlePaginationChange}
							onReEvaluate={handleReEvaluate}
							locked={handleLocked}
							unlocked={handleUnLocked}
							onSetCycle={handleSetCycleOpen}
						/>
					)}
				</div>

				{(openModal.setCycle && cycleUser?.id) && (
					<Modal title="Thiết lập chu kỳ đánh giá" visible={openModal.setCycle} onCancel={() => handleModalClose('setCycle')}>
						<SetCycleModal item={cycleUser} onSuccess={handleSetCycleSuccess} />
					</Modal>
				)}
			</div>
		</Fragment>
	);
}

export default EmployeeEvaluation;
